import {AxiosError, isAxiosError} from 'axios';

type LoopBackErrorPayload = {
  message?: string | {message?: string};
  details?:
    | unknown[]
    | {
        messages?: {
          name?: string[];
        };
      };
};

type AxiosLikeError = {
  response?: {
    data?: {
      message?: string;
      error?: string | LoopBackErrorPayload;
      errors?: Array<{message?: string}>;
      detail?: string;
    };
    status?: number;
    statusText?: string;
  };
  message?: string;
};

function readAxiosLikeError(err: unknown): AxiosLikeError | null {
  if (!err || typeof err !== 'object') {
    return null;
  }
  return err;
}

function isGenericAxiosMessage(message: string): boolean {
  return /^Request failed with status code \d+$/.test(message);
}

function readDetailsMessage(
  details: LoopBackErrorPayload['details'],
): string | null {
  if (Array.isArray(details)) {
    const first = details[0];
    if (typeof first === 'string') {
      return first;
    }
    if (
      first &&
      typeof first === 'object' &&
      'message' in first &&
      typeof (first as {message?: unknown}).message === 'string'
    ) {
      return (first as {message: string}).message;
    }
    return null;
  }

  if (details && typeof details === 'object') {
    const names = details.messages?.name;
    return Array.isArray(names) && names[0] ? names[0] : null;
  }

  return null;
}

function readPayloadMessage(
  message: LoopBackErrorPayload['message'],
): string | null {
  if (typeof message === 'string') {
    return message;
  }
  if (
    message &&
    typeof message === 'object' &&
    typeof message.message === 'string'
  ) {
    return message.message;
  }
  return null;
}

function readLoopBackErrorMessage(
  errorPayload: LoopBackErrorPayload | undefined,
): string | null {
  if (!errorPayload) {
    return null;
  }

  return (
    readDetailsMessage(errorPayload.details) ??
    readPayloadMessage(errorPayload.message)
  );
}

/**
 * Extracts a human-readable message from an unknown error value.
 * Handles Axios errors, LoopBack error payloads, and standard Error objects.
 */
type AxiosLikeResponse = NonNullable<AxiosLikeError['response']>;

function readDataMessage(
  data: AxiosLikeResponse['data'] | undefined,
): string | null {
  const loopBackError =
    typeof data?.error === 'object' ? data.error : undefined;
  const errorsMessage =
    Array.isArray(data?.errors) && data.errors[0]?.message
      ? data.errors[0].message
      : null;

  return (
    readLoopBackErrorMessage(loopBackError) ??
    (typeof data?.message === 'string' ? data.message : null) ??
    (typeof data?.error === 'string' ? data.error : null) ??
    errorsMessage ??
    (typeof data?.detail === 'string' ? data.detail : null)
  );
}

function readStatusFallback(
  response: AxiosLikeResponse | undefined,
): string | null {
  const status = response?.status;
  if (status === 400) {
    return 'The request was rejected. Please check your input and try again.';
  }
  if (status === 409) {
    return 'The request could not be completed because of a conflict.';
  }
  if (status && status >= 500) {
    return 'A server error occurred. Please try again later.';
  }
  if (status && response?.statusText) {
    return `${status} ${response.statusText}`;
  }
  return null;
}

function readNativeErrorMessage(err: unknown): string | null {
  if (
    err instanceof Error &&
    err.message &&
    !isGenericAxiosMessage(err.message)
  ) {
    return err.message;
  }
  return null;
}

export function getErrorMessage(err: unknown): string {
  if (!err) return '';
  if (typeof err === 'string') return err;

  const response = readAxiosLikeError(err)?.response;

  const msg =
    readDataMessage(response?.data) ??
    (err instanceof Error && !response ? err.message : null);

  const formatted = typeof msg === 'string' ? msg.trim() : '';
  if (formatted && !isGenericAxiosMessage(formatted)) {
    return formatted;
  }

  return (
    readStatusFallback(response) ??
    readNativeErrorMessage(err) ??
    'An unexpected error occurred'
  );
}

export function isAxiosHttpError(err: unknown): err is AxiosError {
  if (!isAxiosError(err)) {
    return Boolean(readAxiosLikeError(err)?.response);
  }
  return Boolean(err.response);
}
